import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";

const PAGE_SIZE = 60;

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const sort = params.get("sort") || "newest";
  const minPrice = params.get("min_price") || "";
  const maxPrice = params.get("max_price") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (category) qs.set("category", category);
    if (sort) qs.set("sort", sort);
    if (minPrice) qs.set("min_price", minPrice);
    if (maxPrice) qs.set("max_price", maxPrice);
    qs.set("page", String(page));
    qs.set("limit", String(PAGE_SIZE));

    const countQs = new URLSearchParams();
    if (q) countQs.set("q", q);
    if (category) countQs.set("category", category);
    if (minPrice) countQs.set("min_price", minPrice);
    if (maxPrice) countQs.set("max_price", maxPrice);

    Promise.all([
      api.get(`/products?${qs.toString()}`),
      api.get(`/products/count?${countQs.toString()}`).catch(() => ({ data: { total: 0 } })),
    ])
      .then(([r, c]) => {
        setProducts(r.data);
        setTotal(c.data?.total ?? r.data.length);
      })
      .finally(() => setLoading(false));
    // scroll to top on page change
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }, [q, category, sort, minPrice, maxPrice, page]);

  const set = (key, value) => {
    const np = new URLSearchParams(params);
    if (value) np.set(key, value);
    else np.delete(key);
    // Any filter/sort change resets to page 1
    if (key !== "page") np.delete("page");
    setParams(np, { replace: true });
  };

  const heading = useMemo(() => {
    if (q) return `Results for "${q}"`;
    if (category) return category;
    return "All products";
  }, [q, category]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Build a windowed list of pages: 1 … (page-1) page (page+1) … totalPages
  const pageWindow = useMemo(() => {
    const pages = new Set([1, totalPages, page, page - 1, page + 1]);
    const list = [...pages]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < list.length; i++) {
      out.push(list[i]);
      if (i < list.length - 1 && list[i + 1] - list[i] > 1) out.push("ellipsis-" + i);
    }
    return out;
  }, [page, totalPages]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Marketplace</div>
          <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight">{heading}</h1>
          <p className="text-sm text-neutral-600 mt-2" data-testid="products-total">
            {total} item{total === 1 ? "" : "s"}
            {totalPages > 1 && (
              <span className="text-neutral-400"> · page {page} of {totalPages}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Label className="text-xs uppercase tracking-widest">Sort</Label>
          <Select value={sort} onValueChange={(v) => set("sort", v)}>
            <SelectTrigger data-testid="sort-select" className="w-44 rounded-none border-black">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none border-black">
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price_asc">Price: low → high</SelectItem>
              <SelectItem value="price_desc">Price: high → low</SelectItem>
              <SelectItem value="rating">Top rated</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-10">
        <aside className="space-y-6">
          <div>
            <h3 className="font-display font-bold text-sm uppercase tracking-wider mb-3">Category</h3>
            <ul className="space-y-1 text-sm">
              <li>
                <button
                  data-testid="filter-category-all"
                  onClick={() => set("category", "")}
                  className={`w-full text-left py-1.5 px-2 -mx-2 hover:bg-neutral-100 ${category === "" ? "bg-black text-white hover:bg-black" : ""}`}
                >
                  All categories
                </button>
              </li>
              {categories.map((c) => (
                <li key={c.category_id}>
                  <button
                    data-testid={`filter-category-${c.slug}`}
                    onClick={() => set("category", c.name)}
                    className={`w-full text-left py-1.5 px-2 -mx-2 hover:bg-neutral-100 ${category === c.name ? "bg-black text-white hover:bg-black" : ""}`}
                  >
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-display font-bold text-sm uppercase tracking-wider mb-3">Price</h3>
            <div className="flex gap-2">
              <Input
                data-testid="filter-min-price"
                type="number"
                placeholder="Min"
                value={minPrice}
                onChange={(e) => set("min_price", e.target.value)}
                className="rounded-none border-black h-9"
              />
              <Input
                data-testid="filter-max-price"
                type="number"
                placeholder="Max"
                value={maxPrice}
                onChange={(e) => set("max_price", e.target.value)}
                className="rounded-none border-black h-9"
              />
            </div>
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-square bg-neutral-100 animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="border border-dashed border-neutral-300 p-16 text-center">
              <p className="font-display font-bold text-2xl mb-2">Nothing matches.</p>
              <p className="text-neutral-600">Try a different search or filter.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-10">
                {products.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
              </div>

              {totalPages > 1 && (
                <nav
                  data-testid="pagination"
                  aria-label="Pagination"
                  className="mt-14 flex items-center justify-center gap-2 flex-wrap"
                >
                  <button
                    data-testid="pagination-prev"
                    disabled={page <= 1}
                    onClick={() => set("page", String(page - 1))}
                    className="h-10 px-4 border border-black text-sm font-medium disabled:opacity-30 disabled:cursor-not-allowed hover:bg-black hover:text-white transition-colors"
                  >
                    ← Prev
                  </button>
                  {pageWindow.map((p) =>
                    typeof p === "string" ? (
                      <span key={p} className="h-10 w-10 flex items-center justify-center text-neutral-400">…</span>
                    ) : (
                      <button
                        key={p}
                        data-testid={`pagination-page-${p}`}
                        onClick={() => set("page", String(p))}
                        aria-current={p === page ? "page" : undefined}
                        className={`h-10 min-w-[2.5rem] px-3 border border-black text-sm font-medium transition-colors ${
                          p === page ? "bg-black text-white" : "hover:bg-black hover:text-white"
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}
                  <button
                    data-testid="pagination-next"
                    disabled={page >= totalPages}
                    onClick={() => set("page", String(page + 1))}
                    className="h-10 px-4 border border-black text-sm font-medium disabled:opacity-30 disabled:cursor-not-allowed hover:bg-black hover:text-white transition-colors"
                  >
                    Next →
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
